const study = {
  meta: {
    slug: "sba-acquisition-680k-multi-location-laundromat",
    title:
      "$680K to buy the neighbor's shop: How a laundromat operator used an SBA 7(a) to double locations",
    excerpt:
      "An Ohio laundromat owner had run one location profitably for six years. The strip mall next door came up for sale — the same owner, same equipment, same customer base. Here's how we walked an acquisition SBA 7(a) from LOI to funded in 51 days.",
    industry: "Retail services · Laundromat",
    product: "SBA 7(a) acquisition loan",
    amount: "$680K",
    term: "10 years",
    timeToFund: "51 days",
    state: "Ohio",
    challenge: "Business acquisition financing",
    image:
      "https://images.pexels.com/photos/33866469/pexels-photo-33866469.jpeg?auto=compress&cs=tinysrgb&w=1600",
    date: "2026-06-02",
    readTime: "6 min read",
    tags: ["sba"],
    relatedArticles: ["sba-7a-vs-sba-504", "sba-vs-revenue-financing"],
  },
  situation: `Six years of running one laundromat had built a healthy $340K in annual net operating income. When the owner of the shop next door — a family friend, three doors down — decided to retire, our client had 30 days to sign an LOI or lose the deal to a franchise buyer.

Asking price: **$820K** for the business, equipment, and remaining 7-year lease at below-market rent. Our client had **$140K in cash** and the discipline of six years of clean books. What they didn't have was time — or a bank that would look past "laundromat" as an industry tag.

Two banks passed on the phone. One asked for a full appraisal and 45-day exclusivity we didn't have. That's when the call came to us.`,
  structure: `**Why SBA 7(a), not conventional.** Small-business acquisition is exactly the deal type the SBA program was written for. The 10-year amortization drops the monthly payment to something the combined cash flow could comfortably service, and the 90% government guarantee gets lenders past the fact that laundromats don't have real estate collateral — the equipment is the asset.

We routed the file to a **Preferred Lender Program (PLP)** SBA lender we work with regularly. PLP status matters here: it lets the lender make the credit decision in-house without kicking the file back to the SBA for review. That's the difference between 51 days and 90+ days.

**The file structure:**

- **$680K SBA 7(a)** at Prime + 2.75% (variable), 10-year fully amortizing
- **$140K borrower equity injection** (the SBA requires 10% minimum on acquisitions; we contributed 17% to earn a lower rate)
- **Personal guarantee** required by SBA statute — cannot be waived
- **Life insurance policy** assigned to the lender ($680K face value) — a common SBA condition, cheaper than the borrower expected (~$85/mo for a 45-year-old)

**Documents we drove:**

1. Three years of business tax returns (both locations — we got them from the seller's CPA under an NDA)
2. Trailing 12 months of P&L for the target location, tied to bank statements line by line
3. Business plan showing the combined operating model, staffing, and 3-year projections
4. Purchase agreement with a satisfactory-financing contingency in the borrower's favor

**What we intentionally didn't do:** we didn't try to finance the entire $820K with debt. Overleveraging an acquisition to skip the equity injection is how a great deal turns into a stress-tested one. The 17% equity kept debt service coverage above 1.4x on day one, which every SBA underwriter wants to see.`,
  outcome: `LOI signed on day 3. SBA package submitted on day 11. Lender credit committee approved conditionally on day 27. Final funding wire hit on **day 51**.

By month 6 post-close:

- **Combined net operating income up 22%** vs. the seller's trailing 12 months — cost savings from shared payroll and one bulk-detergent contract
- **Debt service coverage at 1.72x**, comfortably above the 1.25x SBA minimum
- **Both locations retained 100% of key staff** through the transition
- **Owner is already scouting a third location** — with two years of combined operating history, they'll qualify for a second SBA loan or a larger conventional refinance in 2027

The lesson: **SBA acquisitions reward preparation, not speed**. If you're going after a business, start the SBA conversation the day you get serious about the LOI — not after signing. The 51 days from LOI to close only works if the lender is warm before day one.`,
};

export default study;
